import Vue, { Component } from 'vue';
import VueRouter, { RawLocation, Route } from 'vue-router';
import _ from '../../utils';

export type QueryParams = Record<string, string | number | boolean | undefined>;

export const defaultRedirectParamName = 'redirect';

export default class RouterUtils {
  private internalRouter: VueRouter | undefined;

  get router() { return this.internalRouter ?? Vue.$router; }

  constructor(router?: VueRouter) {
    this.internalRouter = router;
  }

  getQueryStringFromUrl(url: string) {
    const match = (/\?(.*?)(?:#|$)/g).exec(url);
    if (match) {
      return match[1];
    }
    return undefined;
  }

  getQueryParamsFromUrl(url: string) {
    const queries = {} as Record<string, string>;
    if (!_.isSet(url)) {
      return queries;
    }
    const queryString = this.getQueryStringFromUrl(url);
    if (queryString) {
      const queryFragments = queryString.split('&');
      queryFragments.forEach((queryFragment) => {
        const queryParts = queryFragment.split('=');
        if (queryParts.length === 2) {
          const val = queryParts[1] as string;
          queries[queryParts[0]] = decodeURIComponent(val);
        }
      });
    }
    return queries;
  }

  /** Returns query parameter for current route and window url (priority in that order) */
  getQueryParams(): Record<string, string> {
    const windowQueries = this.getQueryParamsFromUrl(window?.location?.href);
    const routerQueries = Vue.$router.currentRoute.query as Record<string, string>;
    return { ...windowQueries, ...routerQueries };
  }

  queryParamsToObject(obj: object) {
    const queries = this.getQueryParams();
    Object.keys(queries).forEach((queryName) => {
      _.deepSet(obj, queryName, queries[queryName]);
    });
  }

  updateQueryParams(queries: Record<string, any>, options?: { preserve?: boolean, depth?: number, filterEmptyString?: boolean, filterUndefined?: boolean, filterNull?: boolean }) {
    const filterEmptyString = options?.filterEmptyString ?? true;
    const filterUndefined = options?.filterUndefined ?? true;
    const filterNull = options?.filterNull ?? false;
    const preserve = options?.preserve ?? true;
    const depth = options?.depth;
    const currentPath = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
    const valMap = (val: any) => {
      if (typeof val === 'string') return encodeURIComponent(`${val}`);
      if (val === undefined) return 'undefined';
      if (val === null) return 'null';
      return val;
    };
    let combinedQueries = queries;
    const flatQueries = _.flattenObject(combinedQueries, '.', depth);
    if (preserve) {
      const existingQueries = this.getQueryParams();
      combinedQueries = { ...existingQueries, ...flatQueries };
    }
    const queryString = Object.keys(combinedQueries)
      .filter((qName) => !filterEmptyString || combinedQueries[qName] !== '')
      .filter((qName) => !filterUndefined || combinedQueries[qName] !== undefined)
      .filter((qName) => !filterNull || combinedQueries[qName] !== null)
      .map((qName) => `${qName}=${valMap(combinedQueries[qName])}`)
      .join('&');
    const newPath = `${currentPath}?${queryString}`;
    window.history.replaceState({}, '', newPath);
  }

  /** Returns the first option for the route in a matched component */
  async getRouteOption<T>(route: Route, key: string): Promise<T | undefined> {
    const components = await this.getComponentsForRoute(route) as any[];
    let optionValue: T | undefined;
    const found = components.find((component) => {
      if (component?.options !== undefined) {
        if (component.options[key] !== undefined) {
          optionValue = component.options[key];
          return true;
        }
      } else if (component?.default !== undefined) {
        if (component.default.options !== undefined) {
          if (component.default.options[key] !== undefined) {
            optionValue = component.default.options[key];
            return true;
          }
        }
        if (component?.default[key] !== undefined) {
          optionValue = component.default[key];
          return true;
        }
      } else if (component?.[key] !== undefined) {
        optionValue = component[key];
        return true;
      }
      return false;
    });
    return optionValue;
  }

  /** Returns and resolves matched components from a route */
  async getComponentsForRoute(route: Route) {
    const { matched } = route;
    const components = [] as Component[];
    for (let i = 0; i < matched.length; i += 1) {
      const r = matched[i];
      let component = r.components.default as any;
      if (component?.cid === undefined) {
        if (_.isPromise(component)) {
          // eslint-disable-next-line no-await-in-loop
          component = await component;
        } else if (typeof (component) === 'function') {
          try {
            component = component();
            if (_.isPromise(component)) {
              // eslint-disable-next-line no-await-in-loop
              component = await component;
              if (component?.cid === undefined) {
                component = component?.default;
              }
            }
          } catch (e) {
            Vue.$log.error(e);
          }
        }
      }
      components.push(component);
    }
    return components;
  }

  /**
   * Tests a string to see if it's a url
   * @Returns true if string is a url
   */
  isUrl(stringToTest: string) {
    return stringToTest.match(/^(https?:\/\/|www\.)/g);
  }

  /** If there is a redirect origin, return to that location, otherwise go to the ```alternativeRoute``` */
  returnToRedirectOrigin(alternativeRoute: string, options?: { queryName?: string, usePush?: boolean }) {
    const queryName = options?.queryName ?? defaultRedirectParamName;
    const usePush = options?.usePush ?? false;
    const { [queryName]: redirect, ...query } = this.getQueryParams();
    if (_.isSet(redirect)) {
      if (usePush) {
        this.pushRouteOrUrl(redirect, query);
      } else {
        this.replaceRouteOrUrl(redirect, query);
      }
    } else if (usePush) {
      this.pushRouteOrUrl(alternativeRoute, query);
    } else {
      this.replaceRouteOrUrl(alternativeRoute, query);
    }
  }

  private navigateToRouteOrUrl(routeOrUrl: string, query: QueryParams, routeFunc: ((location: RawLocation) => Promise<Route>), urlFunc: ((url: string) => void)) {
    if (this.isUrl(routeOrUrl)) {
      urlFunc(this.addQueryToUrl(routeOrUrl, query));
    } else {
      if (this.insideRoute(routeOrUrl)) {
        this.updateQueryParams(query);
        // Already in route, so do nothing more
        return;
      }
      const queryAsString: Record<string, string> = {};
      Object.keys(query).forEach((queryKey) => {
        if (_.isSet(query[queryKey])) {
          queryAsString[queryKey] = query[queryKey]!.toString();
        }
      });
      routeFunc({ path: routeOrUrl, query: queryAsString });
    }
  }

  replaceRouteOrUrl(routeOrUrl: string, query: QueryParams = {}) {
    this.navigateToRouteOrUrl(routeOrUrl, query, this.router.replace.bind(this.router), this.replaceUrl);
  }

  pushRouteOrUrl(routeOrUrl: string, query: QueryParams = {}) {
    this.navigateToRouteOrUrl(routeOrUrl, query, this.router.push.bind(this.router), this.pushUrl);
  }

  pushUrl(url: string) {
    if (!url?.match(/^http/)) {
      url = `https://${url}`;
    }
    if (window.location?.assign) {
      window.location.assign(url);
    } else {
      window.location.href = url;
    }
  }

  replaceUrl(url: string) {
    if (!url?.match(/^http/)) {
      url = `https://${url}`;
    }
    if (window.location?.replace) {
      // Unclear if this will work
      // as expected in all browsers since
      // cross origin navigation might actually result in a push instead of replace
      window.location.replace(url);
    } else {
      window.location.href = url;
    }
  }

  /** Avoids duplicate encoding by only encoding if illegal characters are present */
  encodeURIComponentIfNecessary(value: string) {
    if (_.isNil(value)) return value;
    // encodeURI() escapes all characters except: A–Z a–z 0–9 - _ . ! ~ * ' ( )
    // As taken from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURIComponent
    if (value.match(/[^a-zA-Z0-9\-_.!~*'()%]/g)) {
      return encodeURIComponent(value);
    }
    return value;
  }

  addQueryToUrl<T extends string | undefined>(url: T, query: QueryParams = {}): T extends undefined ? string | undefined : string {
    if (_.isNil(url)) return url as any;
    if (_.isEmpty(query)) return url as any;
    const queriesToAdd = { ...Vue.$routerUtils.getQueryParamsFromUrl(url), ...query };
    const baseUrl = url.replace(/\?.*$/, '');
    const queryString = Object.keys(queriesToAdd)
      .filter((key) => _.isSet(queriesToAdd[key]))
      .map((key) => `${key.toLowerCase()}=${this.encodeURIComponentIfNecessary(queriesToAdd[key]!.toString())}`)
      .join('&');
    return `${baseUrl}?${queryString}`;
  }

  /** Adds the current query paramters to the given url */
  addCurrentQueriesToUrl<T extends string | undefined>(url: T): T extends undefined ? string | undefined : string {
    return this.addQueryToUrl(url, { ...Vue.$routerUtils.getQueryParams(), ...Vue.$routerUtils.getQueryParamsFromUrl(url ?? '') });
  }

  /** Redirect to another page and set the redirect-origin,
   * use ```returnToRedirectOrigin``` to return to the place where the redirect originated from */
  async redirect(urlOrRoute: string, redirectQueryName: string = defaultRedirectParamName) {
    if (!_.isSet(urlOrRoute)) {
      Vue.$log.error('Unable to redirect, url or route not set');
      return;
    }
    if (this.isUrl(urlOrRoute)) {
      const urlWithRedirect = this.addQueryToUrl(urlOrRoute, { [redirectQueryName]: window.location.href })!;
      this.pushUrl(urlWithRedirect);
    } else {
      if (this.insideRoute(urlOrRoute)) {
        // Don't redirect if already in route
        return;
      }
      const currentPath = this.router.currentRoute.fullPath;
      const currentQueries = this.router.currentRoute.query;
      await this.router.push({ path: urlOrRoute, query: { [redirectQueryName]: currentPath, ...currentQueries } });
    }
  }

  /**
   * Compares current route with another and normalizes them (removes queries)
   */
  insideRoute(route: string | Route) {
    return this.areRoutesSame(this.router.currentRoute, route);
  }

  /**
   * Compares two routes, ignores query parameters
   */
  areRoutesSame(routeOne: string | Route, routeTwo: string | Route) {
    return this.normalizeRoutePath(routeOne) === this.normalizeRoutePath(routeTwo);
  }

  /**
   * Remove queries, remove
   */
  normalizeRoutePath(route: string | Route) {
    if (route === undefined || route === null) {
      return route;
    }
    let routePath = (route as string);
    if (typeof route !== 'string') {
      routePath = route.fullPath;
    }

    // Remove query string
    let result = routePath.split('?')[0];

    // Remove redundant / from the end of path
    if (result.charAt(result.length - 1) === '/') {
      result = result.slice(0, -1);
    }
    return result;
  }
}
